import React, { useState, useEffect } from "react";
import AddUserForm from "../../components/common/AddUserForm";
import { BiHomeAlt } from "react-icons/bi";
import { GoPlus } from "react-icons/go";
import { FiLoader } from "react-icons/fi";
import { motion } from "framer-motion";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "styles/admin.css";
import TeacherActionsDropdown from "components/dropdown/TeacherActionDropdown";
import TeacherViewModal from "components/modal/TeacherViewModal";

const API_BASE = "https://algoritm-api-edu.uz";

const api = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const AddUser = ({ goBack }) => {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const handleView = async (id) => {
    try {
      setViewLoading(true);
      setViewOpen(true);

      const res = await api.get(`/api/admin/Teacher/GetById/${id}`);
      const teacher = res.data?.data || res.data?.item || res.data;

      if (!teacher?.id) {
        toast.error("Ma'lumot topilmadi");
        setViewOpen(false);
        return;
      }

      setSelectedTeacher({
        id: teacher.id,
        fullName: teacher.fullName || teacher.full_name || "",
        position: teacher.position || "",
        bio: teacher.bio || teacher.description || "",
        imageUrl: teacher.imgUrl
          ? teacher.imgUrl.startsWith("http")
            ? teacher.imgUrl
            : `${API_BASE}/${teacher.imgUrl}`
          : null,
      });
    } catch (err) {
      console.error("View error:", err);
      toast.error("O‘qituvchi ma'lumotlarini yuklab bo‘lmadi");
      setViewOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const fetchTeachers = async () => {
    try {
      setLoading(true);
      const res = await api.post("/api/admin/Teacher/GetAll", {
        page: 1,
        pageSize: 100,
      });

      const list = res.data?.rows || [];
      setTeachers(list);
      console.log("Fetched teachers:", list);
    } catch (err) {
      console.error("Xato:", err);
      setTeachers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeachers();
  }, []);

  const handleAdd = async (formData) => {
    const form = new FormData();
    form.append("FullName", formData.title);
    form.append("Position", formData.position || "");
    form.append("Bio", formData.description || "");
    if (!formData.image) {
      toast.error("Rasm yuklash majburiy!");
      return;
    }
    form.append("Img", formData.image);

    try {
      await api.post("/api/admin/Teacher/Add", form);
      toast.success("O‘qituvchi muvaffaqiyatli qo‘shildi!");
      setShowForm(false);
      fetchTeachers();
    } catch (err) {
      const msg = err.response?.data?.errors
        ? Object.values(err.response.data.errors).flat().join("\n")
        : err.response?.data?.message || "Xato yuz berdi";
      toast.error("Xato: " + msg);
    }
  };

  const handleEdit = async (id) => {
    try {
      const res = await api.get(`/api/admin/Teacher/GetById/${id}`);
      const teacher = res.data?.data || res.data?.item || res.data;

      if (!teacher?.id) {
        toast.error("O‘qituvchi topilmadi");
        return;
      }

      setEditingUser({
        id: teacher.id,
        title: teacher.fullName || teacher.full_name || "",
        position: teacher.position || "",
        description: teacher.bio || teacher.description || "",
        imageUrl: teacher.imgUrl
          ? teacher.imgUrl.startsWith("http")
            ? teacher.imgUrl
            : `${API_BASE}/${teacher.imgUrl}`
          : null,
      });

      setShowForm(true);
    } catch (err) {
      toast.error("Tahrirlash uchun ma'lumot olinmadi");
      console.error(err);
    }
  };

  const handleUpdate = async (formData) => {
    if (!editingUser?.id) return;

    const form = new FormData();
    form.append("FullName", formData.title);
    form.append("Position", formData.position || "");
    form.append("Bio", formData.description || "");
    if (formData.image && typeof formData.image !== "string") {
      form.append("Img", formData.image);
    }

    try {
      await api.put(`/api/admin/Teacher/Update/${editingUser.id}`, form);
      toast.success("Ma'lumotlar muvaffaqiyatli yangilandi!");
      setShowForm(false);
      setEditingUser(null);
      fetchTeachers();
    } catch (err) {
      const msg = err.response?.data?.errors
        ? Object.values(err.response.data.errors).flat().join("\n")
        : "Yangilashda xato";
      toast.error("Xato: " + msg);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Haqiqatan ham o‘chirilsinmi?")) return;
    try {
      await api.delete(`/api/admin/Teacher/Delete/${id}`);
      toast.success("O‘qituvchi muvaffaqiyatli o‘chirildi");
      fetchTeachers();
    } catch (err) {
      toast.error("O‘chirishda xato yuz berdi");
    }
  };

  const handleSubmit = (formData) => {
    editingUser ? handleUpdate(formData) : handleAdd(formData);
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />

      <div className="banner-page-wrapper">
        <motion.div
          className="banner-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="breadcrumb"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            <motion.div
              className="home-icon"
              whileHover={{ rotate: 360, scale: 1.2 }}
              transition={{ duration: 0.4, rotate: 0.5 }}
              onClick={goBack}
            >
              <BiHomeAlt />
            </motion.div>
            <span>/ O‘qituvchilar boshqaruvi</span>
          </motion.div>

          <motion.button
            className="add-btn"
            whileHover={{ scale: 1.05, rotate: 1 }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
            onClick={() => {
              setEditingUser(null);
              setShowForm(true);
            }}
          >
            <GoPlus />
            O‘qituvchi qo‘shish
          </motion.button>
        </motion.div>

        {showForm ? (
          <AddUserForm
            initialData={editingUser}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              setEditingUser(null);
            }}
            requireImage={!editingUser}
          />
        ) : (
          <>
            {loading ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="loading-container"
                style={{ textAlign: "center", padding: "80px" }}
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                  <FiLoader className="spin" size={50} />
                </motion.div>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  Yuklanmoqda...
                </motion.p>
              </motion.div>
            ) : teachers.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="empty-container"
                style={{ textAlign: "center", padding: "100px", color: "#555" }}
              >
                <motion.h2
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  Hozircha o‘qituvchi yo‘q
                </motion.h2>
                <motion.p
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  "O‘qituvchi qo‘shish" tugmasini bosing va birinchisini
                  yarating
                </motion.p>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <motion.table
                  className="banner-table"
                  initial={{ scale: 0.95 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                >
                  <thead>
                    <tr>
                      <th>№</th>
                      <th>Rasm</th>
                      <th>Ism Familya</th>
                      <th>Lavozimi</th>
                      <th>Bio</th>
                      <th>Amallar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teachers.map((t, i) => (
                      <motion.tr
                        key={t.id}
                        layout
                        initial={{
                          opacity: 0,
                          x: -50,
                          scale: 0.9,
                        }}
                        animate={{
                          opacity: 1,
                          x: 0,
                          scale: 1,
                        }}
                        exit={{
                          opacity: 0,
                          x: 50,
                          scale: 0.9,
                        }}
                        transition={{
                          duration: 0.2,
                          type: "spring",
                          stiffness: 100,
                        }}
                        // whileHover={{
                        //   scale: 1.01,
                        // }}
                        className="animated-row"
                      >
                        <motion.td
                          initial={{ x: -20 }}
                          animate={{ x: 0 }}
                          transition={{ delay: i * 0.1 + 0.1 }}
                        >
                          {i + 1}
                        </motion.td>

                        <motion.td
                          initial={{ x: -20 }}
                          animate={{ x: 0 }}
                          transition={{ delay: i * 0.1 + 0.15 }}
                        >
                          <motion.div
                            className="avatar-wrapper"
                            whileHover={{ scale: 1.1, rotate: 5 }}
                            transition={{ duration: 0.2 }}
                          >
                            {t.imgUrl ? (
                              <motion.img
                                src={`${API_BASE}/${t.imgUrl}`}
                                alt="teacher"
                                className="teacher-image"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: i * 0.1 + 0.2 }}
                                whileHover={{ scale: 1.15 }}
                                style={{
                                  width: 60,
                                  height: 60,
                                  borderRadius: 8,
                                  objectFit: "cover",
                                }}
                                onError={(e) =>
                                  (e.target.src =
                                    "https://via.placeholder.com/60")
                                }
                              />
                            ) : (
                              <motion.div
                                className="no-image"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: i * 0.1 + 0.2 }}
                              >
                                —
                              </motion.div>
                            )}
                          </motion.div>
                        </motion.td>

                        <motion.td
                          className="name-cell"
                          initial={{ x: -20 }}
                          animate={{ x: 0 }}
                          transition={{ delay: i * 0.1 + 0.25 }}
                        >
                          <motion.span
                            whileHover={{ color: "#667eea" }}
                            transition={{ duration: 0.2 }}
                          >
                            {t.fullName}
                          </motion.span>
                        </motion.td>

                        <motion.td
                          initial={{ x: -20 }}
                          animate={{ x: 0 }}
                          transition={{ delay: i * 0.1 + 0.3 }}
                        >
                          {t.position || "—"}
                        </motion.td>

                        <motion.td
                          className="bio-cell"
                          initial={{ x: -20 }}
                          animate={{ x: 0 }}
                          transition={{ delay: i * 0.1 + 0.35 }}
                        >
                          <motion.span
                            whileHover={{ color: "#4a5568" }}
                            transition={{ duration: 0.2 }}
                          >
                            {t.bio?.slice(0, 50) || "—"}
                            {t.bio?.length > 50 && "..."}
                          </motion.span>
                        </motion.td>

                        <motion.td>
                          <TeacherActionsDropdown
                            teacher={t}
                            onEdit={() => handleEdit(t.id)}
                            refreshList={fetchTeachers}
                            onView={() => handleView(t.id)}
                          />
                        </motion.td>
                      </motion.tr>
                    ))}
                  </tbody>
                </motion.table>
              </motion.div>
            )}
          </>
        )}
      </div>
      <TeacherViewModal
        isOpen={viewOpen}
        onClose={() => {
          setViewOpen(false);
          setSelectedTeacher(null);
        }}
        teacher={selectedTeacher}
        loading={viewLoading}
      />
    </>
  );
};

export default AddUser;
